import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useStrategyMapService } from "../../../../../../services/strategyMap";
import DependencyCard from "../../../../../../shared/molecules/DependencyCard/DependencyCard";
import { refreshStrategymapView } from "../../../../../../state/actions/strategyMapActions";
import { newPath } from "../../../../../../utils/newPath";
import { StrategyMapDependency, StrategyMapDependencyMapper } from "../../../../../StrategyMapDependencyMapper/StrategyMapDependencyMapper";
import { StrategymapSidePanelSearch } from "../../StrategymapSidePanelSearch";
import { useSearchParams } from "react-router-dom";
import path from "path";

export function StrategymapDependencyTab({
    state,
    strategyMapData
}: {
    state: any,
    strategyMapData: any
}) {
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const currentDepId = searchParams.get("active"); 

    const [expandedCard, setExpandedCard] = useState<string | null>(
      currentDepId,
    );

    const strategyMapService = useStrategyMapService();
    const { dependencySearch, isAddingDep } = state.get;
    const { set: setState } = state;
    const [defaultDependency, setDefaultDepency] = useState<null | {selectedSource: string, selectedDependencies: string[]}>(null);

    const allDependencies = useMemo(() => {
      if(!strategyMapData) return [];
      const deps: StrategyMapDependency[] = [];
      strategyMapData.entities.forEach((project: any) => {
        if (project.dependencies && project.dependencies.length > 0) {
          deps.push({
            id: project.id,
            type: project.type,
            name: project.name || "Unknown",
            _uid: project.id + Math.random().toString(36).substr(2, 9),
            dependency: (project.dependencies || []).map((depItem: any) => {
              const targetProject = strategyMapData.entities.find(
                (p: any) => p.id === depItem.id,
              );
              return {
                id: depItem.id,
                type: depItem.type,
                name: targetProject?.name || "Unknown",
                type: depItem.type ,
                invalid_dependency: depItem.invalid_dependency,
                entity_type: depItem.entity_type,
              };
            }),
          });
        }
      });
      return deps;
    }, [strategyMapData]);

    const filteredDependencies = useMemo(() => {

        if (!dependencySearch) return allDependencies;
        const s = dependencySearch.toLowerCase();
        return allDependencies.filter(dep => 
            dep.name.toLowerCase().includes(s) || 
            dep.dependency.some(d => d.name.toLowerCase().includes(s))
        );
    }, [allDependencies, dependencySearch]);


    const onSmapDependencyExpanded = (open: boolean, depId:string) => {
        setExpandedCard(depId);
        navigate(newPath({active:depId }));
    }

    const onDeleteDependency = async (depId: string) => {
      if (!strategyMapData) return;

      await toast.promise(
        strategyMapService.updateStrategyMap(strategyMapData.id, {
          entities: strategyMapData.entities,
          dependencies: strategyMapData.dependencies.filter((d: any) => d.id !== depId)
        }),
        {
          loading: "Deleting dependency...",
          success: "Dependency deleted successfully!",
          error: "Failed to delete dependency."
        }
      );
      refreshStrategymapView();
    };

    const onDependecyUpdate = async (
      newDep: StrategyMapDependency,
      update_id: string,
    ): Promise<void> => {
      console.log("Dependency saved:", newDep);

      setState((prev: any) => ({ ...prev, isAddingDep: false }));
      await toast.promise(
        strategyMapService.updateStrategyMap(strategyMapData.id, {
          entities: strategyMapData.entities,
          dependencies: strategyMapData.dependencies.map((dep: any) =>
            update_id === dep.id ? newDep : dep,
          ),
        }),
        {
          loading: "Updating strategy map...",
          success: "Strategy map updated successfully!",
          error: "Failed to update strategy map.",
        },
      );

      refreshStrategymapView();
    };

    const onDependecySave = async (
      newDep: StrategyMapDependency,
    ): Promise<void> => {
      console.log("Dependency saved:", newDep);

      setState((prev: any) => ({ ...prev, isAddingDep: false }));
      await toast.promise(
        strategyMapService.updateStrategyMap(strategyMapData.id, {
          entities: strategyMapData.entities,
          dependencies: [...strategyMapData.dependencies, newDep],
        }),
        {
          loading: "Updating strategy map...",
          success: "Strategy map updated successfully!",
          error: "Failed to update strategy map.",
        },
      );

      refreshStrategymapView();
    };

    const EditiorMapper = (props: {
      dependency: {
        selectedSource: string;
        selectedDependencies: string[];
      };
    }) => {
      const { dependency } = props;

      return (
        <StrategyMapDependencyMapper
          data={strategyMapData.entities}
          defaultData={dependency}
          onSave={onDependecySave}
          onUpdate={(newData) =>
            onDependecyUpdate(newData, dependency.selectedSource)
          }
          onCancel={() =>
            setState((prev: any) => ({ ...prev, isAddingDep: false }))
          }
        />
      );
    };

    return (
      <div className="depencyTab-container">
        <div className="depencyTab-header">
          <div className="depencyTab-search-wrapper">
            <StrategymapSidePanelSearch
              searchValue={dependencySearch}
              onSearchChange={(val) =>
                setState((prev: any) => ({ ...prev, dependencySearch: val }))
              }
              placeholder={`Search for dependencies`}
              statusFilters={[]}
              setStatusFilters={() => {}}
              statusDropdownOpen={false}
              setStatusDropdownOpen={() => {}}
              showStatusFilter={false}
            />
          </div>
          <button
            className="depencyTab-add-button"
            onClick={() => {
              setDefaultDepency(null);
              setState((prev: any) => ({
                ...prev,
                isAddingDep: !prev.isAddingDep,
              }));
            }}
          >
            {isAddingDep ? "Cancel" : "+ Add"}
          </button>
        </div>

        <div className="depencyTab-content">
          {strategyMapData && isAddingDep ? (
            <div className="depencyTab-mapper-container">
              <StrategyMapDependencyMapper
                data={strategyMapData.entities}
                defaultData={defaultDependency}
                onSave={onDependecySave}
                onUpdate={onDependecyUpdate}
                onCancel={() =>
                  setState((prev: any) => ({ ...prev, isAddingDep: false }))
                }
              />
            </div>)
          :
            (
          <div className="depencyTab-list-container">
            {filteredDependencies.length > 0
              ? filteredDependencies.map((dep) => {
                return (
                      <DependencyCard
                          key={dep._uid}
                          expanded={dep.id === expandedCard}
                          dependency={dep}
                          onRemove={() => onDeleteDependency(dep.id)}
                          onExpanded={(open) => onSmapDependencyExpanded(open, dep.id)}
                          onEdit={(data) => {
                              //   setDefaultDepency({
                              //     selectedSource: data.id,
                              //     selectedDependencies: data.dependency.map((d) => d.id),
                              //   });
                              //   setState((prev: any) => ({ ...prev, isAddingDep: true }));
                          } }
                          EditorComponent={EditiorMapper} />
                  );
              })
              : !isAddingDep && (
                  <div className="depencyTab-empty-state">
                    <p className="depencyTab-empty-text">No dependencies found.</p>
                  </div>
                )}
          </div>
            )
}
        </div>
      </div>
    );
}